import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { format } from 'date-fns';
import { Box, Text } from 'grommet';
import { ShareRounded } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import { Seo, ShareButton } from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { UsersTablePartial } from '@Components/Partial/Product/CompanyUsers';
import { paths } from '@Components/configs';
import {
  fetchUsersRequest,
  clearUsersRequest,
  createNewUserRequest,
  deleteUserRequest,
  resendUserInviteRequest,
  fetchInviteTokenRequest,
  clearInviteTokenRequest,
} from '@Actions';


const CompanyUsersPage = ({
  small,
  location,
  loading,
  submitLoading,
  fetchCompanyUsers,
  clearCompanyUsers,
  createUser,
  deleteUser,
  resendInvite,
  currentUserEmail,
  fetchInviteToken,
  clearInviteToken,
  inviteToken,
  companyAccountUsers = null,
  paymentPending,
}) => {
  const { theme: { page, button, navigation }, isDarkMode } = useAppTheme();

  React.useEffect(() => {
    fetchCompanyUsers();
    fetchInviteToken();

    return () => {
      clearCompanyUsers();
      clearInviteToken();
    };
  }, []);

  const headerMap = [
    { uid: 'firstName', display: 'First Name', size: small ? '100%' : '15%' },
    { uid: 'lastName', display: 'Last Name', size: small ? '100%' : '15%' },
    { uid: 'email', display: 'Email', size: small ? '100%' : '25%' },
    { uid: 'status', display: 'Status', size: small ? '100%' : '15%' },
    { uid: 'actions', display: 'Actions', size: small ? '100%' : '30%' },
  ];

  const handleDeleteUser = (userId) => {
    if (paymentPending) return;
    deleteUser(userId, paths.users, () => fetchCompanyUsers());
  };

  const handleResendInvite = (userId) => {
    if (paymentPending) return;
    resendInvite(userId, paths.users, () => fetchCompanyUsers());
  };

  const handleCreateUser = (body) => {
    if (paymentPending) return;
    createUser(body, paths.users, () => fetchCompanyUsers());
  };

  const tokenExpiration = inviteToken && new Date(inviteToken.expiresAt);
  const formattedExpiration = tokenExpiration && format(tokenExpiration, 'MM/dd/yyyy hh:mma');
  const usePageBg = isDarkMode ? page.background : page.altBg;

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Manage authorized users',
        subTitle: 'Account Settings',
        bannerChildren: inviteToken?.token && !paymentPending && (
          <Box
            direction={small ? 'column' : 'row'}
            justify={small ? 'start' : 'center'}
            align={small ? 'start' : 'center'}
            gap="0.5rem"
          >
            <ShareButton
              small={small}
              textColor={isDarkMode ? button.primaryText : page.primaryText}
              buttonBg={isDarkMode ? button.hoverBg : page.altBg}
              buttonLabel="Generate invite link"
              renderKey="invite-users"
              copyMessage={`Expires on ${formattedExpiration}`}
              copyValue={`${window.location.origin}/invited?token=${inviteToken.token}`}
              icon={<ShareRounded color={isDarkMode ? button.primaryText : page.primaryText} size="1rem" />}
            />
            {small && (
              <Text size="0.8rem" color={navigation.bannerTextColor}>
                {`Expires on ${formattedExpiration}`}
              </Text>
            )}
          </Box>
        ),
        bannerChildrenPosition: small ? 'bottom' : 'right',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
      >
        <UsersTablePartial
          small={small}
          loading={loading || submitLoading}
          headerMap={headerMap}
          createUser={handleCreateUser}
          deleteUser={handleDeleteUser}
          resendInvite={handleResendInvite}
          usersData={companyAccountUsers}
          currentUserEmail={currentUserEmail}
          paymentPending={paymentPending}
        />
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  const {
    email, accountUsers, inviteToken,
  } = state.auth;

  return {
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    companyAccountUsers: accountUsers,
    currentUserEmail: email,
    inviteToken,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchCompanyUsers: fetchUsersRequest,
    clearCompanyUsers: clearUsersRequest,
    createUser: createNewUserRequest,
    deleteUser: deleteUserRequest,
    resendInvite: resendUserInviteRequest,
    fetchInviteToken: fetchInviteTokenRequest,
    clearInviteToken: clearInviteTokenRequest,
  }, dispatch);
}

CompanyUsersPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchCompanyUsers: PropTypes.func.isRequired,
  clearCompanyUsers: PropTypes.func.isRequired,
  createUser: PropTypes.func.isRequired,
  deleteUser: PropTypes.func.isRequired,
  resendInvite: PropTypes.func.isRequired,
  companyAccountUsers: PropTypes.arrayOf(PropTypes.shape({
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired),
  currentUserEmail: PropTypes.string.isRequired,
  fetchInviteToken: PropTypes.func.isRequired,
  clearInviteToken: PropTypes.func.isRequired,
  inviteToken: PropTypes.shape({
    token: PropTypes.string.isRequired,
    expiresAt: PropTypes.string.isRequired,
  }),
  paymentPending: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(CompanyUsersPage));
